import { useLocalization } from '~/framework';
import { AccordionPanel, Box, Text, Flex, Link, Image } from '@chakra-ui/react';
import { useSingleEntry, GetSingleEntryResponse } from '~/pages/User/Signoffs/[id]/useSingleEntry';
import { format, fromUnixTime } from 'date-fns';
import { WorkData } from '~/monolith-api';

type DataCaptureProps = {
   workOrderEntryId: number;
};

type SignoffStatus = GetSingleEntryResponse['signoffs'][number]['status'];

export function DataCapture({ workOrderEntryId }: DataCaptureProps) {
   const { _js, dateFnsLocale } = useLocalization();
   const singleEntryData = useSingleEntry(workOrderEntryId).data;

   const signoffLabels: Record<SignoffStatus, string> = {
      approved: _js('Approved by: '),
      rejected: _js('Rejected by: '),
   };

   const handleMedias = (workData: WorkData) => {
      if (workData.field_type === 'Image') {
         return <Image src={workData.image_src} width={140} alt={workData.label} />;
      }
      if (workData.field_type === 'Video') {
         if (workData.video_data?.type === 'GuideEmbedObject') {
            const getSource = () => {
               if (workData.video_data?.provider === 'YouTube') {
                  return `https://www.youtube.com/embed/${new URL(
                     workData.video_data.view_url || ''
                  ).searchParams.get('v')}`;
               }
               return workData.video_data?.view_url;
            };
            return (
               <iframe
                  width="420"
                  height="260"
                  frameBorder="0"
                  allowFullScreen
                  src={getSource()}
                  title={workData.video_data.filename}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
               />
            );
         }
         return <video width="300" controls src={workData.video_data?.video_urls.mp4_720} />;
      }
      if (workData.field_type === 'Document') {
         return (
            <Link href={workData.document_src}>
               <Text color="blue.500">{workData.document_filename}</Text>
            </Link>
         );
      }
      return (
         <Box w="70%" textAlign="right">
            {workData.value}
         </Box>
      );
   };

   return (
      <AccordionPanel pb={4}>
         {singleEntryData?.grouped_steps.map(groupedSteps => (
            <>
               {groupedSteps.signoff === null ? (
                  <Box mt={3} fontWeight="bold" key={groupedSteps.signoffNumber}>
                     {_js('Current Signoff')}
                  </Box>
               ) : (
                  <Flex direction="column">
                     <Box mt={3} fontWeight="bold">
                        {_js('Signoff %1', groupedSteps.signoffNumber)}
                     </Box>
                     <Box mt={3}>
                        {signoffLabels[groupedSteps.signoff.status]}
                        <Link
                           href={groupedSteps.signoff.userProfile}
                           color="blue.500"
                           style={{ textDecoration: 'none' }}
                        >
                           {groupedSteps.signoff.username}
                        </Link>
                        <Text>
                           {format(fromUnixTime(groupedSteps.signoff.date), 'MM/dd/yyyy h:mm a', {
                              locale: dateFnsLocale,
                           })}
                        </Text>
                     </Box>
                  </Flex>
               )}
               {groupedSteps.steps.map(step => (
                  <Flex direction="column" key={step.step_number}>
                     {step.work_log.length === 0 && (
                        <Box mt={3}>
                           {_js('Step %1: No data for this step.', `${step.step_number}`)}
                        </Box>
                     )}
                     {step.work_log[0] && <Box mt={3}>{_js('Step %1', `${step.step_number}`)}</Box>}
                     {step.work_log.map(workData => (
                        <Flex
                           direction="row"
                           justifyContent="space-between"
                           mb={1}
                           key={workData.field_revisionid}
                        >
                           <Text variant="muted" mr={15}>
                              {workData.label ?? workData.field_type}
                           </Text>
                           {handleMedias(workData)}
                        </Flex>
                     ))}
                  </Flex>
               ))}
            </>
         ))}
      </AccordionPanel>
   );
}
